import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { userErrorToast } from "@/lib/report-error";
import {
  vagaApprovalExitApi,
  type ApprovalExitCandidate,
  type ApprovalExitDraft,
  type ApprovalExitInput,
  type ApprovalExitResult,
} from "@/services/vaga-approval-exit-api";

interface PendingExit {
  drafts: ApprovalExitDraft[];
  candidates: ApprovalExitCandidate[];
  proceed: () => void;
}

/** "3 candidatos voltam para Selecionados: Ana, Bruno, Carla", one line per stage, in kanban order. */
function candidatesByStage(candidates: ApprovalExitCandidate[]): string[] {
  const stages = new Map<string, string[]>();
  for (const candidate of candidates) {
    const names = stages.get(candidate.etapa_nome) ?? [];
    names.push(candidate.freelancer_nome || "freelancer sem nome");
    stages.set(candidate.etapa_nome, names);
  }
  return [...stages].map(([stage, names]) =>
    `${names.length} ${names.length === 1 ? "candidato volta" : "candidatos voltam"} para ${stage}: ${names.join(", ")}.`,
  );
}

/** What leaving Aprovado did besides moving the vaga, for the success toast. Empty when nothing else changed. */
export function approvalExitSummary(result: ApprovalExitResult): string {
  const drafts = result.rascunhos_excluidos.length;
  const restored = result.candidatos_restaurados.length;
  const parts = [];
  if (drafts > 0) {
    parts.push(drafts === 1 ? "O rascunho de projeto foi excluído." : `${drafts} rascunhos de projeto foram excluídos.`);
  }
  if (restored > 0) {
    parts.push(
      restored === 1
        ? "1 candidato encerrado na aprovação voltou ao processo."
        : `${restored} candidatos encerrados na aprovação voltaram ao processo.`,
    );
  }
  return parts.join(" ");
}

/**
 * Taking a vaga out of Aprovado: the API deletes the drafts created at approval, clears the
 * approval date, brings back the candidates the approval closed out and refuses when a project
 * already started or a draft sits in a proposal.
 * `request` checks first and calls `proceed` once it is clear; `apply` does the move.
 * Render `dialogs` as a sibling of any other dialog.
 */
export function useApprovalExit() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [blockReason, setBlockReason] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingExit | null>(null);

  const request = async (vagaId: string, proceed: () => void) => {
    try {
      const preview = await vagaApprovalExitApi.preview(vagaId);
      if (preview.bloqueio) {
        setBlockReason(preview.bloqueio);
      } else if (preview.rascunhos.length > 0 || preview.candidatos_restaurados.length > 0) {
        setPending({ drafts: preview.rascunhos, candidates: preview.candidatos_restaurados, proceed });
      } else {
        proceed();
      }
    } catch (error) {
      toast(userErrorToast(error, { action: "tirar a vaga de Aprovado", source: "vagas" }));
    }
  };

  const apply = async (vagaId: string, input: ApprovalExitInput): Promise<ApprovalExitResult> => {
    const result = await vagaApprovalExitApi.apply(vagaId, input);
    for (const queryKey of [
      ["controle-projetos"],
      ["freelancer-projetos"],
      ["projetos-andamento-freelancer"],
      ["projetos-andamento"],
      ["vagas-list-page"],
      ["vagas-list-count"],
      ["kanban-cards", vagaId],
      ["kanban-cards-all", vagaId],
      ["kanban-metricas", vagaId],
    ]) {
      queryClient.invalidateQueries({ queryKey });
    }
    return result;
  };

  const dialogs = (
    <>
      <AlertDialog open={!!blockReason} onOpenChange={(open) => !open && setBlockReason(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>A vaga continua em Aprovado</AlertDialogTitle>
            <AlertDialogDescription>{blockReason}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Entendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tirar a vaga de Aprovado?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <ul className="list-disc space-y-1 pl-5">
                  {pending?.drafts.map((draft) => (
                    <li key={draft.id}>
                      O rascunho de projeto de {draft.freelancer_nome || "freelancer sem nome"} será excluído.
                    </li>
                  ))}
                  {candidatesByStage(pending?.candidates ?? []).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <p>A data de aprovação da vaga será apagada.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter em Aprovado</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                pending?.proceed();
                setPending(null);
              }}
            >
              Tirar de Aprovado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { request, apply, dialogs };
}
